import { useCallback, useState } from "react";

import { getExhClient, isExhClientInitialized } from "../client";
import { ExhApiError } from "../types";
import { parseExhError } from "../utils";

interface ValidatePasswordResetArgs {
  hash: string;
  newPassword: string;
}

interface UseExhPasswordResetResult {
  requestPasswordReset: (email: string) => Promise<void>;
  validatePasswordReset: (args: ValidatePasswordResetArgs) => Promise<void>;
  isLoading: boolean;
  error: ExhApiError | null;
}

export const useExhPasswordReset = (): UseExhPasswordResetResult => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<ExhApiError | null>(null);

  const requestPasswordReset = useCallback(async (email: string) => {
    if (!isExhClientInitialized()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const client = getExhClient();
      await client.users.requestPasswordReset(email);
    } catch (err) {
      setError(parseExhError(err));
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const validatePasswordReset = useCallback(async ({ hash, newPassword }: ValidatePasswordResetArgs) => {
    if (!isExhClientInitialized()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const client = getExhClient();
      await client.users.validatePasswordReset({ hash, newPassword });
    } catch (err) {
      setError(parseExhError(err));
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { requestPasswordReset, validatePasswordReset, isLoading, error };
};
